import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import userService from '../services/userService';
import { toast } from 'react-toastify';
import api from '../services/api';

const ViewProfileLayer = ({ userData }) => {
    const [isEditingPassword, setIsEditingPassword] = useState(false);
    const [userDetails, setUserDetails] = useState(userData || {});
    const [loading, setLoading] = useState(!userData);
    const [error, setError] = useState(null);
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    
    const auth = useAuth();
    const { user, setUser } = auth;

    useEffect(() => {
        const fetchUserDetails = async () => {
            if (!user?.id) return;
            
            try {
                setLoading(true);
                const details = await userService.getUser(user.id);
                setUserDetails(prev => ({
                    ...prev,
                    ...details,
                    // Ensure we don't override with null/undefined values
                    ...Object.fromEntries(
                        Object.entries(details).filter(([_, v]) => v != null)
                    )
                }));
                setError(null);
            } catch (err) {
                console.error('Failed to fetch user details:', err);
                setError('Failed to load user details');
            } finally {
                setLoading(false);
            }
        };

        // Only fetch if we don't have userData prop or if user ID changes
        if ((!userData || !Object.keys(userData).length) && user?.id) {
            fetchUserDetails();
        }

    }, [user]);

    // Parse date safely
    const safeDateParse = (dateString) => {
        try {
            return dateString ? new Date(dateString) : null;
        } catch (error) {
            console.error('Error parsing date:', error);
            return null;
        }
    };


    const formatDate = (date) => {
        try {
            if (!date) return 'Never';
            // Handle both string and Date objects
            const dateObj = date instanceof Date ? date : new Date(date);
            // Check if the date is valid
            if (isNaN(dateObj.getTime())) return 'Invalid date';
            return format(dateObj, 'PPpp');
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Date error';
        }
    };

    return (
        <div className="row gy-4">
            <div className="col-lg-4">
                <div className="user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100">
                    <div className="position-relative" style={{ height: '150px', overflow: 'hidden' }}>
                        <img
                            src="assets/images/card/card-bg.png"
                            alt="Profile background"
                            className="w-100 h-100 object-cover"
                        />
                        {/* <div className="position-absolute top-0 end-0 p-3">
                            <span className={`badge ${user?.is_active ? 'bg-success' : 'bg-danger'}`}>
                                {user?.is_active ? 'Active' : 'Inactive'}
                            </span>
                        </div> */}
                    </div>
                    <div className="pb-24 px-4">
                        <div className="text-center mt-n5">
                            <div className="d-flex justify-content-center">
                                <div className="position-relative">
                                    <img
                                        src={user?.profile_image || 'assets/images/user-grid/user-grid-img13.png'}
                                        alt={user?.full_name || 'User'}
                                        className="border border-4 border-white rounded-circle"
                                        style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                                    />
                                </div>
                            </div>
                            
                            <h4 className="mt-3 mb-1">{userDetails?.full_name || user?.full_name || 'No Name'}</h4>
                            <p className="text-muted mb-2">{userDetails?.email || user?.email || 'No email'}</p>
                            <span className="badge bg-primary">
                                {user?.role || 'USER'}
                            </span>
                        </div>

                        <div className="mt-4">
                            <h6 className="mb-3">Account Information</h6>
                            <ul className="list-unstyled p-4">
                                <li className="mb-2">
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted">Status:</span>
                                        <span className={`fw-medium ${user?.is_active ? 'text-success' : 'text-danger'}`}>
                                            {user?.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </li>
                                <li className="mb-2">
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted">2FA Enabled:</span>
                                        <span className={`fw-medium ${user?.is_2fa_enabled ? 'text-success' : 'text-muted'}`}>
                                            {user?.is_2fa_enabled ? 'Yes' : 'No'}
                                        </span>
                                    </div>
                                </li>
                                <li className="mb-2">
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted">Member Since:</span>
                                        <span className="fw-medium">{formatDate(userDetails?.created_at)}</span>
                                    </div>
                                </li>
                                <li className="mb-2">
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted">Last Login:</span>
                                        <span className="fw-medium">{formatDate(userDetails?.last_login)}</span>
                                    </div>
                                </li>
                                {user?.last_2fa_verification && (
                                    <li className="mb-2">
                                        <div className="d-flex justify-content-between">
                                            <span className="text-muted">Last 2FA:</span>
                                            <span className="fw-medium">{formatDate(user?.last_2fa_verification)}</span>
                                        </div>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="col-lg-8">
                <div className="card h-100">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h6 className="card-title mb-0">User Details</h6>
                            <div className="d-flex gap-2 ">
                                {/* <button className="btn btn-sm btn-outline-primary d-flex align-items-center">
                                    <Icon icon="mdi:pencil" className="me-1" />
                                    Edit Profile
                                </button> */}
                            </div>
                        </div>
                        
                        <div className="row g-3 mb-4">
                            <div className="col-md-6">
                                <div className="card h-100 border-0 shadow-sm">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center mb-3">
                                            <div className="bg-primary bg-opacity-10 p-3 rounded-circle me-3">
                                                <Icon icon="mdi:email-outline" className="text-primary" width={24} />
                                            </div>
                                            <div className="d-flex flex-column">
                                                <span className="text-primary-light mb-0 fw-bold">{user?.email || 'N/A'}</span>
                                                <small className="text-muted">Email Address</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card h-100 border-0 shadow-sm">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center mb-3">
                                            <div className="bg-primary bg-opacity-10 p-3 rounded-circle me-3">
                                                <Icon icon="mdi:account-cog-outline" className="text-primary" width={24} />
                                            </div>
                                            <div className="d-flex flex-column">
                                                <span className="text-primary-light mb-0 fw-bold">
                                                    <span className="badge bg-primary">
                                                        {user?.role || 'USER'}
                                                    </span>
                                                </span>
                                                <small className="text-muted">Account Role</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="card h-100 border-0 shadow-sm">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-primary bg-opacity-10 p-3 rounded-circle me-3">
                                                <Icon icon="mdi:calendar-plus" className="text-primary" width={24} />
                                            </div>
                                            <div className="d-flex flex-column">
                                                <span className="text-primary-light mb-0 fw-bold">{formatDate(userDetails?.created_at)}</span>
                                                <small className="text-muted">Account Created</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card h-100 border-0 shadow-sm">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-primary bg-opacity-10 p-3 rounded-circle me-3">
                                                <Icon icon="mdi:update" className="text-primary" width={24} />
                                            </div>
                                            <div className='d-flex flex-column'>
                                                <span className="text-primary-light mb-0 fw-bold">{formatDate(userDetails?.updated_at)}</span>
                                                <small className="text-muted">Last Updated</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>


                        <div className="mt-1">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Password Reset</h6>
                                <button 
                                    className="btn btn-sm btn-outline-primary d-flex align-items-center"
                                    onClick={() => setIsEditingPassword(!isEditingPassword)}
                                >
                                    {isEditingPassword ? (
                                        <>
                                            <Icon icon="mdi:close" className="me-1" size={16} />
                                            Cancel
                                        </>
                                    ) : (
                                        <>
                                            <Icon icon="mdi:lock-reset" className="me-1" size={16} />
                                            Change Password
                                        </>
                                    )}
                                </button>
                            </div>
                            
                            {isEditingPassword && (
                                <div className="card mb-4 border-primary">
                                    <div className="card-body">
                                        <form onSubmit={async (e) => {
                                            e.preventDefault();
                                            
                                            // Check if passwords match
                                            if (passwordData.newPassword !== passwordData.confirmPassword) {
                                                toast.error("New passwords do not match");
                                                return;
                                            }
                                            
                                            try {
                                                // The API function already handles the response parsing
                                                await api.upDatePassword({
                                                    currentPassword: passwordData.currentPassword,
                                                    newPassword: passwordData.newPassword
                                                });
                                                
                                                // Reset form and show success message
                                                setPasswordData({
                                                    currentPassword: '',
                                                    newPassword: '',
                                                    confirmPassword: ''
                                                });
                                                setIsEditingPassword(false);
                                                toast.success('Password updated successfully');
                                                
                                            } catch (error) {
                                                console.error('Error updating password:', error);
                                                // The error message is already formatted by the API service
                                                toast.error(error.message || 'Failed to update password');
                                            }
                                        }}>
                                            <div className="mb-3">
                                                <label htmlFor="currentPassword" className="form-label">Current Password</label>
                                                <div className="input-group">
                                                    <input 
                                                        type={showPasswords.current ? "text" : "password"} 
                                                        className="form-control" 
                                                        id="currentPassword" 
                                                        placeholder="Enter current password"
                                                        value={passwordData.currentPassword}
                                                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                                                        required
                                                    />
                                                    <button 
                                                        className="btn btn-outline-secondary" 
                                                        type="button"
                                                        onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                                                        title={showPasswords.current ? 'Hide password' : 'Show password'}
                                                    >
                                                        <i className={`ri-eye-line`}></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="newPassword" className="form-label">New Password</label>
                                                <div className="input-group">
                                                    <input 
                                                        type={showPasswords.new ? "text" : "password"} 
                                                        className="form-control" 
                                                        id="newPassword" 
                                                        placeholder="Enter new password"
                                                        value={passwordData.newPassword}
                                                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                                                        required
                                                        minLength={8}
                                                    />
                                                    <button 
                                                        className="btn btn-outline-secondary" 
                                                        type="button"
                                                        onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                                                        title={showPasswords.new ? 'Hide password' : 'Show password'}
                                                    >
                                                        <i className={`ri-eye-line`}></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
                                                <div className="input-group">
                                                    <input 
                                                        type={showPasswords.confirm ? "text" : "password"} 
                                                        className={`form-control ${passwordData.newPassword && passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword ? 'is-invalid' : ''}`} 
                                                        id="confirmPassword" 
                                                        placeholder="Confirm new password"
                                                        value={passwordData.confirmPassword}
                                                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                                                        required
                                                    />
                                                    <button 
                                                        className="btn btn-outline-secondary" 
                                                        type="button"
                                                        onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                                                        title={showPasswords.confirm ? 'Hide password' : 'Show password'}
                                                    >
                                                        <i className={`ri-eye-line`}></i>
                                                    </button>
                                                </div>
                                                {passwordData.newPassword && passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                                                    <div className="invalid-feedback d-block">
                                                        Passwords do not match
                                                    </div>
                                                )}
                                            </div>
                                            <div className="d-flex justify-content-end gap-2">
                                                <button 
                                                    type="button" 
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => {
                                                        setPasswordData({
                                                            currentPassword: '',
                                                            newPassword: '',
                                                            confirmPassword: ''
                                                        });
                                                        setIsEditingPassword(false);
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                                <button 
                                                    type="submit" 
                                                    className="btn btn-primary"
                                                    disabled={passwordData.newPassword && passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword}
                                                >
                                                    Update Password
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewProfileLayer;
                                      